import React from 'react'
import { TripResponse } from '@/shared/api/types'
import { WaypointResponse, RouteResponse } from '@/shared/api/routes'
import { ItineraryDaySection } from './ItineraryDaySection'
import { organizeWaypointsByDay } from '../../utils/itineraryHelpers'
import { ArrowLeft, Search } from '@/ui/icons'
import { Button } from '@/ui/components'
import { getTripCoverUrl } from '@/shared/constants'

interface ItineraryViewProps {
  trip: TripResponse
  waypoints: WaypointResponse[]
  waypointsLoading: boolean
  onBack: () => void
  onReorderWaypoints: (waypointIds: string[]) => Promise<void>
  onDeleteWaypoint?: (waypointId: string) => Promise<void>
  onUpdateWaypointDay?: (waypointId: string, day: number) => Promise<void>
  onUpdateWaypoint?: (waypointId: string, updates: { poi_type?: string | null; poi_custom_label?: string | null; name?: string }) => Promise<void>
  onAddWaypointToDay?: (day: number) => void
  route?: RouteResponse | null
  selectedWaypoint?: WaypointResponse | null
  onWaypointSelect?: (waypoint: WaypointResponse) => void
  onOpenPOIDiscovery?: () => void
}

export function ItineraryView({
  trip,
  waypoints,
  waypointsLoading,
  onBack,
  onReorderWaypoints,
  onDeleteWaypoint,
  onUpdateWaypointDay,
  onUpdateWaypoint,
  onAddWaypointToDay,
  route,
  selectedWaypoint,
  onWaypointSelect,
  onOpenPOIDiscovery
}: ItineraryViewProps) {
  const handleBack = () => {
    onBack()
  }

  const bannerImage = getTripCoverUrl(trip.cover_photo_url)

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return ''
    // Parse as local date to avoid timezone issues
    const [year, month, day] = dateString.split('-').map(Number)
    const date = new Date(year, month - 1, day)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const formatDateRange = (): string => {
    if (!trip.start_date && !trip.end_date) return ''
    if (trip.start_date && trip.end_date) {
      const start = formatDate(trip.start_date)
      const end = formatDate(trip.end_date)
      return `${start} - ${end}`
    }
    return trip.start_date ? formatDate(trip.start_date) : formatDate(trip.end_date)
  }

  const getTripDuration = (): number | null => {
    if (!trip.start_date || !trip.end_date) return null
    const start = new Date(trip.start_date)
    const end = new Date(trip.end_date)
    const diffTime = end.getTime() - start.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1 // +1 to include both start and end days
    return diffDays
  }

  // Organize waypoints by day
  const itineraryDays = organizeWaypointsByDay(waypoints, trip.start_date, trip.end_date)
  const totalDays = itineraryDays.length

  // Handle adding waypoint to specific day
  const handleAddWaypointToDay = (day: number) => {
    // For now, this will trigger adding a waypoint via map click
    // The waypoint will be assigned to the specified day
    // This can be enhanced later to open a dialog or trigger map click mode
    if (onAddWaypointToDay) {
      onAddWaypointToDay(day)
    }
  }

  // Waypoint numbers restart at 1 for each day
  if (waypointsLoading && waypoints.length === 0) {
    return (
      <div className="h-full flex flex-col bg-adventure-50">
        <div className="relative h-32 bg-gray-200">
          <img
            src={bannerImage}
            alt={trip.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center px-4">
            <h1 className="text-heading-lg text-white mb-1">{trip.name}</h1>
          </div>
          <button
            onClick={handleBack}
            className="absolute top-4 left-4 w-8 h-8 rounded-full bg-white/90 hover:bg-white flex items-center justify-center transition-colors"
            aria-label="Back to trips"
          >
            <ArrowLeft className="w-4 h-4 text-earth-900" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          <div className="flex items-center justify-center py-12">
            <div className="text-body-sm text-earth-600">Loading itinerary...</div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col bg-adventure-50">
      {/* Header with banner */}
      <div className="relative h-32 bg-gray-200">
        <img
          src={bannerImage}
          alt={trip.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center px-4">
          <h1 className="text-heading-lg text-white mb-1">{trip.name}</h1>
          {(trip.start_date || trip.end_date) && (
            <div className="text-body-sm text-white/90 text-center">
              <span>{formatDateRange()}</span>
              {getTripDuration() && (
                <span className="ml-2">({getTripDuration()} {getTripDuration() === 1 ? 'day' : 'days'})</span>
              )}
            </div>
          )}
        </div>
        <button
          onClick={handleBack}
          className="absolute top-4 left-4 w-8 h-8 rounded-full bg-white/90 hover:bg-white flex items-center justify-center transition-colors"
          aria-label="Back to trips"
        >
          <ArrowLeft className="w-4 h-4 text-earth-900" />
        </button>
        {/* Trip Tools Button */}
        {onOpenPOIDiscovery && (
          <div className="absolute top-4 right-4">
            <Button
              onClick={onOpenPOIDiscovery}
              variant="default"
              size="sm"
              className="bg-white/90 hover:bg-white text-earth-900"
            >
              <Search className="w-4 h-4 mr-2" />
              Discover Places
            </Button>
          </div>
        )}
      </div>

      {/* Day-organized itinerary */}
      <div className="flex-1 overflow-y-auto p-4">
        {itineraryDays.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center px-4">
            <p className="text-body-md text-earth-600 mb-2">No days in this trip yet</p>
            <p className="text-body-sm text-earth-500">
              Add start and end dates to your trip to see day-by-day itinerary
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {itineraryDays.map((day) => {
              // Waypoint numbers restart at 1 for each day
              return (
                <ItineraryDaySection
                  key={day.day}
                  day={day}
                  startWaypointNumber={1}
                  onAddWaypoint={handleAddWaypointToDay}
                  onDeleteWaypoint={onDeleteWaypoint}
                  onUpdateWaypointDay={onUpdateWaypointDay}
                  onUpdateWaypoint={onUpdateWaypoint}
                  onReorderWaypoints={onReorderWaypoints}
                  allWaypoints={waypoints}
                  totalDays={totalDays}
                  selectedWaypointId={selectedWaypoint?.id || null}
                  onWaypointSelect={onWaypointSelect}
                />
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
